/**
 * Browser-side API for the Lab Worker.
 *
 * The page is served from sandbase.ai/lab/*, so it shares localStorage with the
 * main SandBase web app. The login token written there (key `token`) is sent as
 * a Bearer header to the Lab Worker, which forwards it to apiserver for the
 * current request only. Lab never stores the token anywhere else.
 */
const TOKEN_KEY = 'token'
const ACTIVE_ORG_KEY = 'active_org_id'

export function readToken(): string | null {
  try { return window.localStorage.getItem(TOKEN_KEY) } catch { return null }
}

/** Mirrors the main app's session expiry so the next sandbase.ai page shows signed-out state. */
function expireSession(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
    window.localStorage.removeItem(ACTIVE_ORG_KEY)
  } catch { /* privacy mode */ }
  window.dispatchEvent(new Event('lab:unauthorized'))
}

import { loginBridgeUrl } from './login-url'

/** Lab bridge checks the current session before sending anyone through OAuth. */
export function loginUrl(returnPath: string): string {
  return loginBridgeUrl(returnPath)
}

export class LabApiError extends Error {
  constructor(readonly status: number, message: string, readonly reason?: string) {
    super(message)
  }
}

interface Envelope<T> { code?: number; data?: T; message?: string; reason?: string }

export async function labApi<T>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
  if (!path.startsWith('/lab/api/')) throw new Error(`Lab API path must start with /lab/api/: ${path}`)
  const { auth, ...rest } = init
  const headers = new Headers(rest.headers)
  headers.set('accept', 'application/json')
  if (auth) {
    const token = readToken()
    if (!token) throw new LabApiError(401, 'Sign in to run your own Pelican Test.', 'sign_in_required')
    headers.set('Authorization', `Bearer ${token}`)
  }
  const response = await fetch(path, { ...rest, headers, credentials: 'same-origin' })
  const body = await response.json().catch(() => null) as Envelope<T> | null
  if (!response.ok || !body || body.data === undefined) {
    if (response.status === 401 && auth) expireSession()
    throw new LabApiError(response.status, body?.message || 'Something went wrong. Please try again.', body?.reason)
  }
  return body.data
}

/**
 * Starts a run. The Worker executes it inside this request and streams NDJSON
 * progress; the run keeps going only while this page stays open. Resolves with
 * the run id as soon as the first event arrives and keeps draining the stream.
 */
export async function startLabRun(scene: string, input: unknown): Promise<string> {
  const token = readToken()
  if (!token) throw new LabApiError(401, 'Sign in to run your own test.', 'sign_in_required')
  const response = await fetch(`/lab/api/run/${scene}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
    credentials: 'same-origin',
    keepalive: false,
  })
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => null) as Envelope<unknown> | null
    if (response.status === 401) expireSession()
    throw new LabApiError(response.status, body?.message || 'Could not start the run.', body?.reason)
  }
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) throw new LabApiError(502, 'The run ended before it started.')
    buffer += value
    const line = buffer.split('\n')[0]
    if (buffer.includes('\n')) {
      const event = JSON.parse(line) as { type: string; data?: { id?: string } }
      if (event.type !== 'run' || !event.data?.id) throw new LabApiError(502, 'Unexpected response from Lab.')
      // Keep reading in the background: the run lives as long as this request.
      void (async () => { try { for (;;) { if ((await reader.read()).done) break } } catch { /* page left */ } })()
      return event.data.id
    }
  }
}

export const jsonBody = (method: string, value: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(value),
})
